/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { type PropsWithChildren } from 'react';

import { jsx } from '@compiled/react';
import Markdown from 'markdown-to-jsx';

import { Text } from '@atlaskit/primitives/compiled';
import {
	type SearchResultConversationPartial,
	type SearchResultMessagePartial,
} from '@atlassian/dynamic-cards-search-client';

import { removeMarkdown } from '../../utils/remove-markdown';

import { Channel } from './channel';
import { Mention, UserMention } from './mention';

/**
 * The whitespace in between aliases <!here> <!channel> is removed
 * This constant prevent the alias from being pushed together ex: "@here@channel"
 * We are using this constant rather than adding a space through CSS because it adds
 * a real space character that does not break up the text when selecting.
 * */
const SPACE = ' ';

export const processMessageBody = (inputString: string): string => {
	if (!inputString) {
		return '';
	}

	return (
		removeMarkdown(inputString)
			//Slack User
			.replace(/<@(\w+)>/g, '<Mention>$1</Mention>')
			//Teams User
			.replace(/<@([a-zA-Z0-9-]+)>/g, '<Mention>$1</Mention>')
			//Slack Channel
			.replace(/<#(\w+)\|?>/g, '<Channel>$1</Channel>')
			//Teams Channel variation 1
			.replace(
				/<#teamId\/[a-f0-9-]+\/channelId\/teamId\/([a-f0-9-]+)\/channelId\/([0-9a-zA-Z:@._-]+)>/g,
				'<Channel>teamId/$1/channelId/$2</Channel>',
			)
			// Teams Channel variation 2
			.replace(
				/<[@#]teamId\/([a-f0-9-]+)\/channelId\/([0-9a-zA-Z:@._-]+)>/g,
				'<Channel>teamId/$1/channelId/$2</Channel>',
			)
			.replace(/<\!subteam\^(\w+)>/g, '<Subteam>$1</Subteam>')
			.replace(/<\!(\w+)>/g, '<Alias>$1</Alias>')
			// emojis, works for those that have repeating pattern as well. eg: :word-word-word:
			.replace(/:((@@@hl@@@.+?@@@endhl@@@)(-@@@hl@@@.+?@@@endhl@@@)*):/g, (match) => {
				const words =
					match
						.match(/@@@hl@@@(.+?)@@@endhl@@@/g)
						?.map((m) => m.replace(/@@@hl@@@|@@@endhl@@@/g, '')) || [];
				return `<Highlight>:${words.join('-')}:</Highlight>`;
			})
			.replace(/@@@hl@@@(.+?)@@@endhl@@@/g, '<Highlight>$1</Highlight>')
			.replace(/<@<Highlight>(.+?)<\/Highlight>>/g, '<Highlight><Mention>$1</Mention></Highlight>')
			.replace(
				/<#<Highlight>(.+?)<\/Highlight>\|>/g,
				'<Highlight><Channel>$1</Channel></Highlight>',
			)
	);
	// .replace(/\:(\w+)\:/g, '<Emoji>$1</Emoji>') Support for emojis coming soon
};

const PlainText = ({ children }: PropsWithChildren<void>) => <>{children}</>;
const Highlight = ({ children }: PropsWithChildren<void>) => (
	<Text as="strong">
		{SPACE}
		{children}
	</Text>
);

// It is assumed that description is the full message and excerpt is a potentially truncated version
export const completeExcerptIfTruncated = (excerpt: string, description: string): string => {
	if (excerpt.length === 0 || description.length === 0 || excerpt === description) {
		return excerpt;
	}

	var modifiedExcerpt = excerpt;

	// regex which has the starting tag of a mention, but not the end
	const startTagRegex = /<[@#][^> ]*$/g;
	const startTagMatch = modifiedExcerpt.match(startTagRegex);

	if (startTagMatch) {
		// theoretically, there should only be one match at max
		const partialMention = startTagMatch[startTagMatch.length - 1];
		// the partialMention can appear several times in the string. We need to find the correct one
		const partialMentionInExcerptCount = excerpt.split(partialMention).length - 1;
		const splitDescription = description.split(partialMention);
		// check if partial mention is found in description
		if (splitDescription.length >= 2) {
			const splitByClosingTag = splitDescription[partialMentionInExcerptCount].split('>');
			// check if closing tag is found
			if (splitByClosingTag.length >= 2) {
				const restOfString = splitByClosingTag[0];
				modifiedExcerpt = modifiedExcerpt + restOfString + '>';
			}
		}
	}

	// const endTagRegex = /^[^< ]*\.tacv2>/g; This works on channel mentions that I have observed
	// weaker regex which tries to capture the ending tag of a mention (assuming it always ends with >), but not the start
	const weakEndTagRegex = /^[^< ]*>/g;
	const weakEndTagMatch = modifiedExcerpt.match(weakEndTagRegex);

	if (weakEndTagMatch) {
		// theoretically, there should only be one match at max
		const partialMention = weakEndTagMatch[0];
		const splitDescription = description.split(partialMention);
		// partialMention not found in description
		if (splitDescription.length >= 2) {
			const splitByOpeningTag = splitDescription[0].split('<');
			// opening tag found
			if (splitByOpeningTag.length >= 2) {
				const restOfString = splitByOpeningTag[splitByOpeningTag.length - 1];
				modifiedExcerpt = '<' + restOfString + modifiedExcerpt;
			}
		}
	}

	return modifiedExcerpt;
};

export const MessageBody = ({
	partial,
}: {
	partial: SearchResultMessagePartial | SearchResultConversationPartial;
}) => {
	// in ConversationSearchResult, partial.excerpt is used as the body
	if (partial.excerpt) {
		partial.excerpt = completeExcerptIfTruncated(
			partial.excerpt,
			partial?.entity?.description ?? '',
		);
	}
	const body = partial.excerpt || partial?.entity?.description || '';

	/**
	 * Event hough we remove markdown from the body, we still use this component
	 * to render mentions and highlighted text */
	return (
		<Markdown
			options={{
				forceInline: true,
				overrides: {
					a: PlainText,
					Highlight,
					Alias: Mention,
					Mention: {
						component: UserMention,
						props: {
							associations: partial?.entity?.associatedWith?.edges,
						},
					},
					Channel: {
						component: Channel,
						props: {
							associations: partial?.entity?.associatedWith?.edges,
						},
					},
				},
			}}
		>
			{processMessageBody(body)}
		</Markdown>
	);
};
